import { TodoTypeDto } from '@act/shared/data-transfer-objects';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Box, IconButton, TableCell, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { HighlightableRow } from 'toolkit/tables/highlightable-row';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { TodoDtoWithMember } from './docket-types';
import { StringHelper } from '@act/shared/utilities';
import { displayName } from './utilities/displayNameAndBirthdayCheck.utils';
import { MemberStatusTag } from '@act/storybook';

interface FollowUpItemTableRowProps {
  data: TodoDtoWithMember;
  openAdditionalMenu: (event, Todo) => void;
}

const formattedDateTimeFromTodo = (todo: TodoDtoWithMember) => {
  let timeMillis;

  if (todo.call) {
    timeMillis = todo.call.timestamp;
  } else if (todo.feedbackCall) {
    timeMillis = todo.feedbackCall.timestamp;
  } else if (todo.message) {
    timeMillis = todo.message.timestamp;
  } else if (todo.visit) {
    timeMillis = todo.visit.timestamp;
  } else {
    return '––';
  }

  return DateTime.fromMillis(timeMillis).toFormat('LLL dd, h:mma');
};

const taskFormatMap: { [key in TodoTypeDto]?: string } = {
  [TodoTypeDto.INCOMPLETE_CALL]: 'Complete call',
  [TodoTypeDto.INCOMPLETE_NOTE_CALL]: 'Complete note',
  [TodoTypeDto.INCOMPLETE_NOTE_VISIT]: 'Complete note',
  [TodoTypeDto.INCOMPLETE_VISIT]: 'Complete visit',
  [TodoTypeDto.CALL_BACK]: 'Call back',
  [TodoTypeDto.TEXT_BACK]: 'Text back',
  [TodoTypeDto.CALL_AUDIT]: 'Review call',
};
function formatTaskType(data: TodoDtoWithMember) {
  const taskType = data.type;
  return taskFormatMap[taskType] || taskType;
}

const FollowUpItemTableRow = ({
  data,
  openAdditionalMenu,
}: FollowUpItemTableRowProps) => {
  const navigate = useAppNavigation();

  // Check if this is a call without a member associated
  const isCallWithoutMember =
    (data.externalItemType === 'call' ||
      data.externalItemType === 'feedback_call') &&
    !data.member;

  function formatEventType(data: TodoDtoWithMember) {
    const { externalItemType: itemType } = data;

    if (itemType === 'call' || itemType === 'message') {
      return StringHelper.sentenceCase(
        `${data[itemType]?.incoming ? 'incoming' : 'outgoing'} ${itemType}`
      );
    }

    if (itemType === 'feedback_call') {
      return 'Incoming call';
    }

    return StringHelper.sentenceCase(itemType);
  }

  function formatMemberName(data: TodoDtoWithMember): string[] {
    if (data?.member?.name) {
      return [displayName(data.member)];
    }

    const unknownName = 'Unknown';

    if (
      data?.externalItemType === 'call' ||
      data?.externalItemType === 'feedback_call'
    ) {
      const type = data.externalItemType === 'call' ? 'call' : 'feedbackCall';
      const phoneNumber = data[type]?.incoming
        ? data[type]?.originator?.phoneNumber
        : data[type]?.receiver?.phoneNumber;
      return [unknownName, phoneNumber];
    }

    return [unknownName];
  }

  const [memberName, phoneNumber] = formatMemberName(data);

  return (
    <HighlightableRow
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (data.subjectId && data.userId) {
          navigate.toMemberPage(data.userId, data.subjectId);
        } else if (
          data.type === TodoTypeDto.CALL_AUDIT ||
          isCallWithoutMember
        ) {
          navigate.toCalls({ onlyUnassignedCalls: true });
        }
      }}
      className={data.type}
    >
      <TableCell>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="bodyMedium">{memberName}</Typography>
            {!!phoneNumber && (
              <Typography variant="bodySmall">{phoneNumber}</Typography>
            )}
          </Box>
          <Box>
            {data.feedbackCall && data.feedbackCall.incoming && (
              <FeedbackIcon
                color="primary"
                sx={{ marginLeft: '8px', marginBottom: '4px' }}
              />
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <MemberStatusTag status={data?.member?.memberStatus} />
      </TableCell>
      <TableCell>
        <Typography variant="bodyMedium">{formatEventType(data)}</Typography>
      </TableCell>

      <TableCell>
        <Typography variant="bodyMedium">{formatTaskType(data)}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="bodyMedium">
          {formattedDateTimeFromTodo(data)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <IconButton
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            openAdditionalMenu(e, data);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </TableCell>
      <TableCell align="right">
        {(data.subjectId && data.userId) ||
        data.type === TodoTypeDto.CALL_AUDIT ||
        isCallWithoutMember ? (
          <IconButton
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              if (data.subjectId && data.userId) {
                navigate.toMemberPage(data.userId, data.subjectId);
              } else {
                navigate.toCalls({ onlyUnassignedCalls: true });
              }
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        ) : null}
      </TableCell>
    </HighlightableRow>
  );
};

export default FollowUpItemTableRow;
